import React, { useState, useEffect } from 'react';
import AdminTable from 'common/AdminTable';
import TruncatedMessage from 'common/TruncatedMessage';
import { getRequest, patchRequest, deleteRequest } from 'apis/ApiInstence';
import axiosInstance from 'apis/axiosConfig';
import { FaTrash, FaEdit, FaEye } from 'react-icons/fa';
import { getDiscountedPrice, getOfferPercent } from 'common/pricing';

const BACKEND = process.env.REACT_APP_BACKEND;

const sanitizeDecimalValue = (value) => {
  if (value === null || value === undefined) return '';
  const normalized = String(value).trim().replace(/[^0-9.]/g, '');
  const parts = normalized.split('.');

  if (parts.length <= 1) return normalized;

  return `${parts[0]}.${parts.slice(1).join('')}`;
};

const ProductList = () => {
  const [productData, setProductData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [imageFiles, setImageFile] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const ageOptions = ['0-1', '1-2', '2-3', '3-4', '4+'];

  const fetchProducts = async () => {
    try {
      const res = await getRequest('products');
      setProductData(res?.data || []);
    } catch (err) {
      console.error('Failed to fetch products:', err);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [categoryRes, subcategoryRes] = await Promise.all([
          axiosInstance.get('/categories'),
          axiosInstance.get('/subcategories'),
        ]);
        setCategories(Array.isArray(categoryRes.data) ? categoryRes.data : []);
        setSubcategories(Array.isArray(subcategoryRes.data) ? subcategoryRes.data : []);
      } catch (error) {
        console.error('Failed to load category options:', error);
      }
    };

    fetchOptions();
  }, []);

  const openModal = (product, viewOnly = false) => {
    setCurrentProduct(product);
    setIsViewMode(viewOnly);
    setImageFile([]);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!currentProduct.id) return alert('Missing product ID!');
    const sanitizedWeight = sanitizeDecimalValue(currentProduct.weight);

    if (currentProduct.weight && !sanitizedWeight) {
      return alert('Weight must be a numeric value, for example 2 or 2.5');
    }

    const formData = new FormData();

    formData.append('name', currentProduct.name || '');
    formData.append('cost_price', currentProduct.cost_price || '');
    formData.append('selling_price', currentProduct.selling_price || '');
    formData.append('offer', currentProduct.offer || '');
    formData.append('description', currentProduct.description || '');
    formData.append('stock_quantity', currentProduct.stock_quantity || '');
    formData.append('dimensions', currentProduct.dimensions || '');
    formData.append('category', currentProduct.category || '');
    formData.append('subcategory', currentProduct.subcategory || '');
    formData.append('age_group', currentProduct.age_group || '');
    formData.append('weight', sanitizedWeight);
    formData.append('status', currentProduct.status || '');

    if (imageFiles.length > 0) {
      imageFiles.forEach((file) => formData.append('images', file));
    }

    try {
      await patchRequest(`products/${currentProduct.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      fetchProducts();
      setShowModal(false);
    } catch (err) {
      console.error('Failed to update product:', err?.response?.data || err);
    }
  };

  const handleRemove = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await deleteRequest(`products/${id}`);
      fetchProducts();
    } catch (err) {
      console.error('Failed to delete product:', err);
    }
  };

  const tableHeaders = [
    {
      Header: 'Image',
      accessor: 'images',
      Cell: ({ row }) => {
        let list = row.original.images;
        if (!list) return <span>No Image</span>;
        if (typeof list === 'string') {
          try {
            list = JSON.parse(list);
          } catch {
            list = list.split(',').map((i) => i.trim());
          }
        }
        return (
          <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
            {list.map((img, i) => (
              <img
                key={i}
                src={`${BACKEND}uploads/${img}`}
                alt={`img-${i}`}
                width={50}
                height={50}
                style={{ objectFit: 'cover', borderRadius: 4 }}
              />
            ))}
          </div>
        );
      },
    },
    { Header: 'Name', accessor: 'name' },
    {
      Header: 'Price',
      accessor: 'selling_price',
      Cell: ({ row }) => {
        const originalPrice = Number(row.original.selling_price || 0);
        const discountedPrice = getDiscountedPrice(row.original.selling_price, row.original.offer);
        const offerPercent = getOfferPercent(row.original.offer);

        if (!originalPrice) return 'Coming Soon';

        return offerPercent ? `Rs. ${discountedPrice} (${offerPercent}% off)` : `Rs. ${originalPrice}`;
      },
    },
    {
      Header: 'Offer',
      accessor: 'offer',
      Cell: ({ row }) => {
        const offerPercent = getOfferPercent(row.original.offer);
        return offerPercent ? `${offerPercent}% off` : '-';
      },
    },
    { Header: 'Stock', accessor: 'stock_quantity' },
    { Header: 'Category', accessor: 'category' },
    { Header: 'Subcategory', accessor: 'subcategory' },
    { Header: 'Age Group', accessor: 'age_group' },
    {
      Header: 'Description',
      accessor: 'description',
      Cell: ({ row }) => (
        <TruncatedMessage text={row?.original?.description} maxLength={10} />
      ),
    },
    {
      Header: 'Actions',
      accessor: 'actions',
      Cell: ({ row }) => (
        <>
          <button
            className="btn btn-sm mx-1 text-white"
            style={{ backgroundColor: '#17a2b8' }}
            onClick={() => openModal(row.original, true)}
          >
            <FaEye />
          </button>
          <button
            className="btn btn-sm mx-1 text-white"
            style={{ backgroundColor: '#ffc107' }}
            onClick={() => openModal(row.original, false)}
          >
            <FaEdit />
          </button>
          <button
            className="btn btn-sm mx-1 text-white"
            style={{ backgroundColor: '#dc3545' }}
            onClick={() => handleRemove(row.original.id)}
          >
            <FaTrash />
          </button>
        </>
      ),
    },
  ];

  return (
    <>
      <div className="container-fluid mt-5">
        <div className="mainheadig mx-4">
          <h4 className="text-white font-weight-bold">Toy Product List</h4>
        </div>

        {productData?.length ? (
          <AdminTable tableHeaders={tableHeaders} tableData={productData} />
        ) : (
          <div className="text-white mx-4">No product records found.</div>
        )}
      </div>

      {showModal && currentProduct && (
        <div className="modal fade show" style={{ display: 'block' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 shadow">
              <div className="modal-header bg-light">
                <h5 className="modal-title fw-semibold text-dark">
                  {isViewMode ? 'See All Details' : 'Update Product'}
                </h5>
                <button
                  className="close btn text-danger"
                  onClick={() => setShowModal(false)}
                  style={{ fontSize: '1.5rem' }}
                >
                  &times;
                </button>
              </div>

              <div className="modal-body">
                {isViewMode ? (
                  <>
                    {[
                      'cost_price',
                      'offer',
                      'dimensions',
                      'weight',
                      'category',
                      'subcategory',
                      'description',
                    ].map((field) => (
                      <div className="form-group mb-2" key={field}>
                        <label htmlFor={field} className="fw-semibold text-secondary">
                          {field.replace('_', ' ').toUpperCase()}
                        </label>
                        <input
                          id={field}
                          type="text"
                          className="form-control"
                          style={{
                            borderColor: '#a1572f',
                            boxShadow: 'none',
                            backgroundColor: '#ffffff',
                            color: '#000000',
                          }}
                          value={currentProduct[field] || ''}
                          readOnly
                        />
                      </div>
                    ))}
                  </>
                ) : (
                  <>
                    <div className="row">
                      {[
                        'name',
                        'cost_price',
                        'selling_price',
                        'stock_quantity',
                        'dimensions',
                        'weight',
                      ].map((field) => (
                        <div className="col-md-4 mb-2" key={field}>
                          <label htmlFor={field} className="fw-semibold text-secondary">
                            {field.replace('_', ' ').toUpperCase()}
                          </label>
                          <input
                            id={field}
                            type={/price|quantity|weight/.test(field) ? 'number' : 'text'}
                            className="form-control"
                            style={{ borderColor: '#a1572f', boxShadow: 'none' }}
                            value={currentProduct[field] || ''}
                            onChange={(e) =>
                              setCurrentProduct({ ...currentProduct, [field]: e.target.value })
                            }
                          />
                        </div>
                      ))}

                      <div className="col-md-4 mb-2">
                        <label htmlFor="offer" className="fw-semibold text-secondary">
                          OFFER (%)
                        </label>
                        <div className="input-group">
                          <input
                            id="offer"
                            type="text"
                            className="form-control"
                            style={{ borderColor: '#a1572f', boxShadow: 'none' }}
                            value={currentProduct.offer || ''}
                            onChange={(e) =>
                              setCurrentProduct({ ...currentProduct, offer: e.target.value })
                            }
                            placeholder="Enter offer like 20 or 20%"
                          />
                          <div className="input-group-append">
                            <span className="input-group-text">%</span>
                          </div>
                        </div>
                      </div>

                      <div className="col-md-4 mb-2">
                        <label htmlFor="age_group" className="fw-semibold text-secondary">
                          AGE GROUP
                        </label>
                        <select
                          id="age_group"
                          className="form-control"
                          style={{ borderColor: '#a1572f', boxShadow: 'none' }}
                          value={currentProduct.age_group || ''}
                          onChange={(e) =>
                            setCurrentProduct({ ...currentProduct, age_group: e.target.value })
                          }
                        >
                          <option value="">Select age group</option>
                          {ageOptions.map((age) => (
                            <option key={age} value={age}>
                              {age}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-md-4 mb-2">
                        <label htmlFor="category" className="fw-semibold text-secondary">
                          CATEGORY
                        </label>
                        <select
                          id="category"
                          className="form-control"
                          style={{ borderColor: '#a1572f', boxShadow: 'none' }}
                          value={currentProduct.category || ''}
                          onChange={(e) =>
                            setCurrentProduct({
                              ...currentProduct,
                              category: e.target.value,
                              subcategory: '',
                            })
                          }
                        >
                          <option value="">Select category</option>
                          {categories.map((item) => (
                            <option key={item.id} value={item.name}>
                              {item.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-md-4 mb-2">
                        <label htmlFor="subcategory" className="fw-semibold text-secondary">
                          SUBCATEGORY
                        </label>
                        <select
                          id="subcategory"
                          className="form-control"
                          style={{ borderColor: '#a1572f', boxShadow: 'none' }}
                          value={currentProduct.subcategory || ''}
                          onChange={(e) =>
                            setCurrentProduct({
                              ...currentProduct,
                              subcategory: e.target.value,
                            })
                          }
                          disabled={!currentProduct.category}
                        >
                          <option value="">Select subcategory</option>
                          {subcategories
                            .filter((item) => item.parent === currentProduct.category)
                            .map((item) => (
                              <option key={item.id} value={item.name}>
                                {item.name}
                              </option>
                            ))}
                        </select>
                      </div>

                      <div className="col-12 mb-2">
                        <label htmlFor="description" className="fw-semibold text-secondary">
                          DESCRIPTION
                        </label>
                        <textarea
                          id="description"
                          rows={2}
                          className="form-control"
                          style={{ borderColor: '#a1572f', boxShadow: 'none' }}
                          value={currentProduct.description || ''}
                          onChange={(e) =>
                            setCurrentProduct({ ...currentProduct, description: e.target.value })
                          }
                        />
                      </div>
                    </div>

                    <div className="form-group mb-2">
                      <label htmlFor="product-image" className="fw-semibold text-secondary">
                        Image
                      </label>
                      <input
                        id="product-image"
                        type="file"
                        multiple
                        className="form-control"
                        onChange={(e) => setImageFile(Array.from(e.target.files))}
                        style={{ borderColor: '#a1572f' }}
                      />

                      <div className="mt-2">
                        {imageFiles.length > 0 ? (
                          <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                            {imageFiles.map((file, i) => (
                              <img
                                key={i}
                                src={URL.createObjectURL(file)}
                                width={50}
                                height={50}
                                className="border rounded"
                                alt={`preview-${i}`}
                                style={{ objectFit: 'cover' }}
                              />
                            ))}
                          </div>
                        ) : currentProduct.images ? (
                          (() => {
                            let imgs = currentProduct.images;

                            if (typeof imgs === 'string') {
                              try {
                                imgs = JSON.parse(imgs);
                              } catch {
                                imgs = imgs.split(',').map((i) => i.trim());
                              }
                            }

                            if (!Array.isArray(imgs)) imgs = [];

                            return imgs.length > 0 ? (
                              <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                                {imgs.map((img, i) => (
                                  <img
                                    key={i}
                                    src={`${BACKEND}uploads/${img}`}
                                    width={50}
                                    height={50}
                                    className="border rounded"
                                    alt={`img-${i}`}
                                    style={{ objectFit: 'cover' }}
                                  />
                                ))}
                              </div>
                            ) : (
                              <span className="text-muted">No preview</span>
                            );
                          })()
                        ) : (
                          <span className="text-muted">No preview</span>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="modal-footer">
                <button className="btn btn-danger rounded" onClick={() => setShowModal(false)}>
                  Close
                </button>
                {!isViewMode && (
                  <button
                    className="btn rounded text-white"
                    style={{ backgroundColor: '#a1572f', borderColor: '#a1572f' }}
                    onClick={handleSave}
                  >
                    Save changes
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ProductList;
